/**
 * OnboardingChecklist — dismissible "getting started" card on the admin
 * Dashboard (Jun 2026 UX pass). Auto-hides once the three core steps are
 * done, or when dismissed. State comes from GET /api/onboarding/status.
 */
import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { useAuth } from '../context/AuthContext';
import { CheckCircle2, Circle, X, Rocket, ChevronRight } from 'lucide-react';

const API = `${window.__BACKEND_URL__}/api`;

export const OnboardingChecklist = () => {
  const navigate = useNavigate();
  const { user, company, token, isSuperUser } = useAuth();
  const [status, setStatus] = useState(null);
  const dismissKey = `ht_onboarding_dismissed_${company?.id || 'c'}`;
  const [dismissed, setDismissed] = useState(() => localStorage.getItem(dismissKey) === '1');

  useEffect(() => {
    if (dismissed || user?.role !== 'admin') return;
    axios.get(`${API}/onboarding/status`, {
      headers: token ? { Authorization: `Bearer ${token}` } : undefined,
    })
      .then((res) => setStatus(res.data))
      .catch(() => setStatus(null));
  }, [dismissed, user?.role, token]);

  if (dismissed || user?.role !== 'admin' || !status || status.all_core_done) return null;

  const steps = [
    { key: 'staff_added', label: 'Add your staff', hint: 'Invite workers and supervisors', to: '/manage-users' },
    { key: 'team_created', label: 'Create a team', hint: 'Group workers under a supervisor', to: '/manage-teams' },
    { key: 'job_created', label: 'Create your first job', hint: 'Schedule work and assign it', to: '/jobs' },
    ...(isSuperUser ? [{ key: 'qbo_connected', label: 'Connect QuickBooks', hint: 'Optional — sync invoices & payroll', to: '/settings?section=quickbooks', optional: true }] : []),
    { key: 'kiosk_created', label: 'Set up a kiosk', hint: 'Optional — on-site clock-in tablet', to: '/manage-teams', optional: true },
  ];
  const done = steps.filter((s) => status[s.key]).length;

  const dismiss = () => {
    localStorage.setItem(dismissKey, '1');
    setDismissed(true);
  };

  return (
    <div
      className="ht-card p-5 relative"
      style={{ border: '1px solid rgba(30,136,229,0.45)', background: 'linear-gradient(135deg, rgba(30,136,229,0.10), rgba(0,200,150,0.06))' }}
      data-testid="onboarding-checklist"
    >
      <button
        type="button"
        onClick={dismiss}
        className="absolute top-3 right-3 p-1.5 rounded-lg hover:bg-white/10 transition-colors"
        style={{ color: '#94A3B8' }}
        title="Dismiss — you can finish these anytime"
        data-testid="onboarding-dismiss-btn"
      >
        <X className="w-4 h-4" />
      </button>
      <div className="flex items-center gap-2 mb-1">
        <Rocket className="w-5 h-5" style={{ color: '#1E88E5' }} />
        <h3 className="text-white font-semibold">Get {company?.company_name || 'your company'} up and running</h3>
      </div>
      <p className="text-sm mb-4" style={{ color: '#94A3B8' }}>
        {done} of {steps.length} steps done — knock these out and your crew can start clocking time today.
      </p>
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-2">
        {steps.map((s) => {
          const isDone = !!status[s.key];
          return (
            <button
              key={s.key}
              type="button"
              onClick={() => !isDone && navigate(s.to)}
              disabled={isDone}
              className={`flex items-start gap-2.5 p-3 rounded-xl text-left transition-all ${isDone ? 'opacity-60 cursor-default' : 'hover:bg-white/5 cursor-pointer'}`}
              style={{ border: '1px solid #2D3748', background: '#0F1923' }}
              data-testid={`onboarding-step-${s.key}`}
            >
              {isDone
                ? <CheckCircle2 className="w-5 h-5 flex-shrink-0 mt-0.5" style={{ color: '#00C896' }} />
                : <Circle className="w-5 h-5 flex-shrink-0 mt-0.5" style={{ color: '#4A5568' }} />}
              <span className="min-w-0 flex-1">
                <span className={`block text-sm font-medium ${isDone ? 'line-through' : ''} text-white`}>
                  {s.label}{s.optional && <span className="text-[10px] ml-1.5 font-normal" style={{ color: '#64748B' }}>optional</span>}
                </span>
                <span className="block text-xs mt-0.5" style={{ color: '#94A3B8' }}>{s.hint}</span>
              </span>
              {!isDone && <ChevronRight className="w-4 h-4 flex-shrink-0 mt-1" style={{ color: '#64748B' }} />}
            </button>
          );
        })}
      </div>
    </div>
  );
};

export default OnboardingChecklist;
